import { useState } from "react";
import { cn } from "../../lib/utils";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

const SIZE: Record<AvatarSize, string> = {
  xs: "size-6 text-[10px]",
  sm: "size-8 text-[11px]",
  md: "size-10 text-xs",
  lg: "size-12 text-sm",
  xl: "size-16 text-base",
  "2xl": "size-20 text-2xl",
};

// eslint-disable-next-line react-refresh/only-export-components
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** v3 getCompanyInitials: first letters of the first two words, else first two letters. */
function companyInitialsOf(name: string): string {
  const words = name.replace(/[^a-zA-Z0-9\s]/g, " ").trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  const stripped = name.replace(/[\s\W]+/g, "");
  return (stripped.slice(0, 2) || name.trim().slice(0, 2) || "CO").toUpperCase();
}

/** v3 getCompanyColor palette (emerald … rose), picked by string hash. */
const COMPANY_PALETTE = [
  "bg-emerald-600",
  "bg-blue-600",
  "bg-violet-600",
  "bg-pink-600",
  "bg-amber-600",
  "bg-teal-600",
  "bg-sky-600",
  "bg-fuchsia-600",
  "bg-cyan-600",
  "bg-orange-600",
  "bg-indigo-600",
  "bg-rose-600",
];

function companyColorOf(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0;
  }
  return COMPANY_PALETTE[Math.abs(hash) % COMPANY_PALETTE.length];
}

export interface AvatarProps {
  name: string;
  src?: string | null;
  size?: AvatarSize;
  /** Square with rounded corners — for companies and clients. */
  square?: boolean;
  className?: string;
}

/** Photo with an initials fallback; falls back again if the image fails. */
export function Avatar({
  name,
  src,
  size = "md",
  square = false,
  className,
}: AvatarProps) {
  const [broken, setBroken] = useState(false);
  const showImage = !!src && !broken;

  return (
    <span
      role="img"
      aria-label={name}
      className={cn(
        "relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden font-bold",
        square
          ? cn("rounded-xl border border-black/10 text-white shadow-2xs", companyColorOf(name))
          : "rounded-full bg-surface-3 text-ink-muted",
        SIZE[size],
        className
      )}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          onError={() => setBroken(true)}
          className="size-full object-cover"
        />
      ) : square ? (
        companyInitialsOf(name)
      ) : (
        initialsOf(name)
      )}
    </span>
  );
}

export interface AvatarStackProps {
  people: { name: string; src?: string | null }[];
  max?: number;
  /** Real head-count when `people` is only a sample (the "+N" uses it). */
  total?: number;
  size?: Extract<AvatarSize, "xs" | "sm" | "md">;
  className?: string;
}

/** Overlapping avatars with a "+N" chip, as on project and pool cards. */
export function AvatarStack({
  people,
  max = 4,
  total,
  size = "sm",
  className,
}: AvatarStackProps) {
  const shown = people.slice(0, max);
  const rest = Math.max(total ?? people.length, people.length) - shown.length;

  return (
    <div className={cn("flex items-center", className)}>
      <div className="flex -space-x-2">
        {shown.map((p, i) => (
          <Avatar
            key={`${p.name}-${i}`}
            name={p.name}
            src={p.src}
            size={size}
            className="ring-2 ring-white"
          />
        ))}
      </div>
      {rest > 0 && (
        <span className="ml-2 text-[11px] font-semibold text-ink-muted">
          +{rest}
        </span>
      )}
    </div>
  );
}
